import { useState, useCallback, useRef } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import MapboxSelector from '../components/MapboxSelector';
import ImageTagEditor from '../components/ImageTagEditor';
import {
  Home,
  Maximize2,
  Settings,
  Image as ImageIcon,
  Check,
  ChevronRight,
  Info,
  Upload,
  Camera,
  Building,
  Droplets,
  Wifi,
  Shield,
  TreePine,
  Flame,
  Thermometer,
  Car,
  Bath,
  BedDouble,
  Layers,
  Users,
  Droplet,
  Zap,
  Lock,
  Sun,
  Wind,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

export default function CrearPropiedad() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [fotoPrincipalArchivo, setFotoPrincipalArchivo] = useState(null);
  const [coordenadas, setCoordenadas] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);

  // Estado para imágenes con tags
  const [imagenesConTags, setImagenesConTags] = useState([]);

  // Estado inicial con TODAS las características del modelo
  const [data, setData] = useState({
    titulo: '',
    descripcion: '',
    ubicacion: '',
    precio: '',
    zona: 'Centro',
    caracteristicas: {
      // Características numéricas
      recamaras: 3,
      banos: 2,
      medios_banos: 1,
      area: 100,
      terreno: 200,
      estacionamiento: 1,
      niveles: 1,

      // Amenidades booleanas (estructura de la casa)
      jardin: false,
      terraza: false,
      alberca: false,
      elevador: false,
      cisterna: false,
      bodega: false,
      cuarto_servicio: false,
      acceso_discapacitados: false,
      paneles_solares: false,
      aire_acondicionado: false,
      ventiladores: false,
      calentador_agua: false,
      gas_estacionario: false,
      amueblado: false,
      cocina_integral: false,
      cocina_equipada: false,
      vigilancia: false,
      seguridad: false,
      cerca_electrica: false,
      interfon: false,
      internet: false,
      fibra_optica: false,
      pet_friendly: false,
      uso_comercial: false,

      // Información técnica
      estado_conservacion: 'bueno',
      antiguedad: 'nueva',
      tipo_construccion: 'tradicional',
      orientacion: '',
      tipo_agua: '',
      tipo_operacion: 'venta',
    },
  });

  // Grupos de características para organización
  const gruposCaracteristicas = [
    {
      titulo: 'Distribución',
      icono: <Layers size={18} />,
      campos: [
        {
          nombre: 'recamaras',
          label: 'Recámaras',
          tipo: 'number',
          min: 0,
          icono: <BedDouble size={16} />,
        },
        {
          nombre: 'banos',
          label: 'Baños Completos',
          tipo: 'number',
          min: 0,
          icono: <Bath size={16} />,
        },
        {
          nombre: 'medios_banos',
          label: 'Medios Baños',
          tipo: 'number',
          min: 0,
          icono: <Droplet size={16} />,
        },
        {
          nombre: 'niveles',
          label: 'Niveles',
          tipo: 'number',
          min: 1,
          icono: <Building size={16} />,
        },
      ],
    },
    {
      titulo: 'Dimensiones',
      icono: <Maximize2 size={18} />,
      campos: [
        { nombre: 'area', label: 'Área Construcción (m²)', tipo: 'number', min: 0, icono: null },
        { nombre: 'terreno', label: 'Área Terreno (m²)', tipo: 'number', min: 0, icono: null },
        {
          nombre: 'estacionamiento',
          label: 'Estacionamientos',
          tipo: 'number',
          min: 0,
          icono: <Car size={16} />,
        },
      ],
    },
    {
      titulo: 'Amenidades Exteriores',
      icono: <TreePine size={18} />,
      campos: [
        { nombre: 'jardin', label: 'Jardín', tipo: 'checkbox', icono: <TreePine size={16} /> },
        { nombre: 'terraza', label: 'Terraza', tipo: 'checkbox', icono: <Sun size={16} /> },
        { nombre: 'alberca', label: 'Alberca', tipo: 'checkbox', icono: <Droplets size={16} /> },
        { nombre: 'patio', label: 'Patio', tipo: 'checkbox', icono: null },
      ],
    },
    {
      titulo: 'Comodidades Interiores',
      icono: <Home size={18} />,
      campos: [
        {
          nombre: 'aire_acondicionado',
          label: 'Aire Acondicionado',
          tipo: 'checkbox',
          icono: <Thermometer size={16} />,
        },
        {
          nombre: 'ventiladores',
          label: 'Ventiladores',
          tipo: 'checkbox',
          icono: <Wind size={16} />,
        },
        {
          nombre: 'calentador_agua',
          label: 'Calentador de Agua',
          tipo: 'checkbox',
          icono: <Flame size={16} />,
        },
        {
          nombre: 'gas_estacionario',
          label: 'Gas Estacionario',
          tipo: 'checkbox',
          icono: <Flame size={16} />,
        },
        { nombre: 'amueblado', label: 'Amueblado', tipo: 'checkbox', icono: <Home size={16} /> },
        {
          nombre: 'cuarto_servicio',
          label: 'Cuarto de Servicio',
          tipo: 'checkbox',
          icono: <Users size={16} />,
        },
        { nombre: 'bodega', label: 'Bodega', tipo: 'checkbox', icono: <Building size={16} /> },
      ],
    },
    {
      titulo: 'Cocina',
      icono: <Thermometer size={18} />,
      campos: [
        { nombre: 'cocina_integral', label: 'Cocina Integral', tipo: 'checkbox', icono: null },
        { nombre: 'cocina_equipada', label: 'Cocina Equipada', tipo: 'checkbox', icono: null },
      ],
    },
    {
      titulo: 'Seguridad',
      icono: <Shield size={18} />,
      campos: [
        {
          nombre: 'vigilancia',
          label: 'Vigilancia 24/7',
          tipo: 'checkbox',
          icono: <Shield size={16} />,
        },
        {
          nombre: 'seguridad',
          label: 'Sistema de Seguridad',
          tipo: 'checkbox',
          icono: <Lock size={16} />,
        },
        {
          nombre: 'cerca_electrica',
          label: 'Cerca Eléctrica',
          tipo: 'checkbox',
          icono: <Zap size={16} />,
        },
        { nombre: 'interfon', label: 'Interfon', tipo: 'checkbox', icono: null },
      ],
    },
    {
      titulo: 'Conectividad',
      icono: <Wifi size={18} />,
      campos: [
        { nombre: 'internet', label: 'Internet', tipo: 'checkbox', icono: <Wifi size={16} /> },
        {
          nombre: 'fibra_optica',
          label: 'Fibra Óptica',
          tipo: 'checkbox',
          icono: <Wifi size={16} />,
        },
      ],
    },
    {
      titulo: 'Accesibilidad',
      icono: <Users size={18} />,
      campos: [
        { nombre: 'elevador', label: 'Elevador', tipo: 'checkbox', icono: <Building size={16} /> },
        {
          nombre: 'acceso_discapacitados',
          label: 'Acceso Discapacitados',
          tipo: 'checkbox',
          icono: <Users size={16} />,
        },
        { nombre: 'pet_friendly', label: 'Pet Friendly', tipo: 'checkbox', icono: null },
      ],
    },
    {
      titulo: 'Sostenibilidad',
      icono: <Sun size={18} />,
      campos: [
        {
          nombre: 'paneles_solares',
          label: 'Paneles Solares',
          tipo: 'checkbox',
          icono: <Sun size={16} />,
        },
        { nombre: 'cisterna', label: 'Cisterna', tipo: 'checkbox', icono: <Droplets size={16} /> },
      ],
    },
  ];

  // Opciones para selects
  const opcionesEstadoConservacion = [
    { valor: 'nuevo', label: 'Nuevo' },
    { valor: 'excelente', label: 'Excelente' },
    { valor: 'bueno', label: 'Bueno' },
    { valor: 'regular', label: 'Regular' },
    { valor: 'requiere_remodelacion', label: 'Requiere Remodelación' },
  ];

  const opcionesAntiguedad = [
    { valor: 'nueva', label: 'Menos de 1 año' },
    { valor: '1-5', label: '1-5 años' },
    { valor: '5-10', label: '5-10 años' },
    { valor: '10-20', label: '10-20 años' },
    { valor: '20+', label: 'Más de 20 años' },
  ];

  const opcionesTipoConstruccion = [
    { valor: 'tradicional', label: 'Tradicional' },
    { valor: 'contemporanea', label: 'Contemporánea' },
    { valor: 'moderna', label: 'Moderna' },
    { valor: 'rustica', label: 'Rústica' },
    { valor: 'industrial', label: 'Industrial' },
  ];

  const opcionesTipoAgua = [
    { valor: 'municipal', label: 'Municipal' },
    { valor: 'pozo', label: 'Pozo' },
    { valor: 'ambas', label: 'Ambas' },
  ];

  const opcionesTipoOperacion = [
    { valor: 'venta', label: 'Venta' },
    { valor: 'renta', label: 'Renta' },
    { valor: 'renta_venta', label: 'Renta con opción a compra' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaracteristicaChange = (e) => {
    const { name, value, type, checked } = e.target;
    setData((prev) => ({
      ...prev,
      caracteristicas: {
        ...prev.caracteristicas,
        [name]: type === 'checkbox' ? checked : value,
      },
    }));
  };

  const handleSelectChange = (name, value) => {
    setData((prev) => ({
      ...prev,
      caracteristicas: {
        ...prev.caracteristicas,
        [name]: value,
      },
    }));
  };

  const determinarZona = (lat) => {
    if (lat > 21.0) return 'Norte';
    if (lat > 20.9) return 'Centro';
    return 'Sur';
  };

  const handleSeleccionarUbicacion = useCallback(
    (coords, direccion) => {
      setCoordenadas(coords);
      if (direccion && !data.ubicacion) {
        setData((prev) => ({ ...prev, ubicacion: direccion }));
      }
      const zona = determinarZona(coords[1]);
      setData((prev) => ({ ...prev, zona }));
    },
    [data.ubicacion]
  );

  const subirImagen = async (archivo) => {
    const formData = new FormData();
    formData.append('imagenes', archivo);

    try {
      const res = await api.post('/properties/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.urls[0];
    } catch (err) {
      console.error('Error subiendo imagen:', err);
      throw new Error(`Error subiendo imagen: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || submitLockRef.current) {
      return;
    }

    if (!coordenadas) {
      return setError('Por favor, selecciona una ubicación en el mapa.');
    }

    if (!data.titulo || !data.ubicacion || !data.precio) {
      return setError('Por favor, completa los campos obligatorios.');
    }

    if (!fotoPrincipalArchivo) {
      return setError('Por favor, selecciona una imagen principal.');
    }

    setIsSubmitting(true);
    submitLockRef.current = true;
    setError('');
    setMensaje('Publicando propiedad...');

    try {
      console.log('Iniciando creación de propiedad...');
      console.log('Número de imágenes con tags:', imagenesConTags.length);
      console.log('Imágenes con tags:', imagenesConTags);

      // Subir foto principal
      let fotoPrincipalUrl = '';
      if (fotoPrincipalArchivo) {
        console.log('Subiendo foto principal...');
        fotoPrincipalUrl = await subirImagen(fotoPrincipalArchivo);
        console.log('Foto principal subida:', fotoPrincipalUrl);
      }

      // Subir imágenes con tags
      const imagenesConUrls = [];
      for (const imagen of imagenesConTags) {
        try {
          console.log('Procesando imagen:', imagen);
          if (imagen.file) {
            console.log('Subiendo imagen secundaria:', imagen.file.name);
            const url = await subirImagen(imagen.file);
            console.log('Imagen subida:', url);
            imagenesConUrls.push({
              url,
              descripcion: imagen.descripcion || '',
              tags: imagen.tags || [],
              orden: imagen.orden || 0,
            });
          } else if (imagen.url) {
            // Si ya tiene URL (viene de edición)
            console.log('Usando imagen existente:', imagen.url);
            imagenesConUrls.push({
              url: imagen.url,
              descripcion: imagen.descripcion || '',
              tags: imagen.tags || [],
              orden: imagen.orden || 0,
            });
          }
        } catch (err) {
          console.error(`Error subiendo imagen:`, err);
        }
      }

      console.log('Total imágenes a enviar:', imagenesConUrls.length);
      console.log('Imágenes procesadas:', imagenesConUrls);

      // Preparar características con tipos correctos
      const caracteristicasFormateadas = {
        // Campos numéricos
        recamaras: parseInt(data.caracteristicas.recamaras) || 0,
        banos: parseInt(data.caracteristicas.banos) || 0,
        medios_banos: parseInt(data.caracteristicas.medios_banos) || 0,
        area: parseFloat(data.caracteristicas.area) || 0,
        terreno: parseFloat(data.caracteristicas.terreno) || 0,
        estacionamiento: parseInt(data.caracteristicas.estacionamiento) || 0,
        niveles: parseInt(data.caracteristicas.niveles) || 1,

        // Campos booleanos
        jardin: Boolean(data.caracteristicas.jardin),
        terraza: Boolean(data.caracteristicas.terraza),
        alberca: Boolean(data.caracteristicas.alberca),
        elevador: Boolean(data.caracteristicas.elevador),
        cisterna: Boolean(data.caracteristicas.cisterna),
        bodega: Boolean(data.caracteristicas.bodega),
        cuarto_servicio: Boolean(data.caracteristicas.cuarto_servicio),
        acceso_discapacitados: Boolean(data.caracteristicas.acceso_discapacitados),
        paneles_solares: Boolean(data.caracteristicas.paneles_solares),
        aire_acondicionado: Boolean(data.caracteristicas.aire_acondicionado),
        ventiladores: Boolean(data.caracteristicas.ventiladores),
        calentador_agua: Boolean(data.caracteristicas.calentador_agua),
        gas_estacionario: Boolean(data.caracteristicas.gas_estacionario),
        amueblado: Boolean(data.caracteristicas.amueblado),
        cocina_integral: Boolean(data.caracteristicas.cocina_integral),
        cocina_equipada: Boolean(data.caracteristicas.cocina_equipada),
        vigilancia: Boolean(data.caracteristicas.vigilancia),
        seguridad: Boolean(data.caracteristicas.seguridad),
        cerca_electrica: Boolean(data.caracteristicas.cerca_electrica),
        interfon: Boolean(data.caracteristicas.interfon),
        internet: Boolean(data.caracteristicas.internet),
        fibra_optica: Boolean(data.caracteristicas.fibra_optica),
        pet_friendly: Boolean(data.caracteristicas.pet_friendly),
        uso_comercial: Boolean(data.caracteristicas.uso_comercial),

        // Campos de texto
        estado_conservacion: data.caracteristicas.estado_conservacion || 'bueno',
        antiguedad: data.caracteristicas.antiguedad || 'nueva',
        tipo_construccion: data.caracteristicas.tipo_construccion || 'tradicional',
        orientacion: data.caracteristicas.orientacion || '',
        tipo_agua: data.caracteristicas.tipo_agua || '',
        tipo_operacion: data.caracteristicas.tipo_operacion || 'venta',
      };

      const propiedadData = {
        titulo: data.titulo,
        descripcion: data.descripcion,
        ubicacion: data.ubicacion,
        precio: parseFloat(data.precio),
        foto_principal: fotoPrincipalUrl,
        imagenes: imagenesConUrls,
        pos_x: coordenadas[0],
        pos_y: coordenadas[1],
        zona: data.zona,
        caracteristicas: caracteristicasFormateadas,
      };

      console.log('Enviando datos al servidor:', propiedadData);

      const response = await api.post('/properties', propiedadData);
      console.log('Respuesta del servidor:', response.data);

      setMensaje('✅ Propiedad creada exitosamente');
      setTimeout(() => navigate('/propiedades'), 2000);
    } catch (err) {
      console.error('Error completo creando propiedad:', err);
      console.error('Respuesta del error:', err.response?.data);
      setError(err.response?.data?.error || 'Error al crear la propiedad');
      setMensaje('');
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-28 pb-20 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <header className="mb-12">
          <span className="text-blue-600 text-xs font-bold uppercase tracking-[0.3em] mb-2 block">
            Panel de Gestión
          </span>
          <h1 className="text-4xl font-serif italic text-slate-900">Nueva Propiedad</h1>
          <p className="text-slate-500 mt-2">
            Completa todos los detalles para listar el inmueble en el catálogo global.
          </p>
        </header>

        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-700 border border-red-100 rounded-2xl">
            <div className="flex items-center gap-3">
              <AlertCircle size={18} />
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {mensaje && (
          <div className="mb-8 p-4 bg-green-50 text-green-700 border border-green-100 rounded-2xl">
            <div className="flex items-center gap-3">
              <CheckCircle size={18} />
              <span className="font-medium">{mensaje}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Tarjeta 1: Información Básica */}
          <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                <Home size={20} />
              </div>
              <h2 className="font-serif text-xl">Información General</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Título */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Título Comercial *
                </label>
                <input
                  type="text"
                  name="titulo"
                  placeholder="Ej. Residencia Minimalista en el Norte"
                  value={data.titulo}
                  onChange={handleChange}
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                />
              </div>

              {/* Precio */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Precio (MXN) *
                </label>
                <input
                  type="number"
                  name="precio"
                  placeholder="0.00"
                  value={data.precio}
                  onChange={handleChange}
                  required
                  min="0"
                  step="1000"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                />
              </div>

              {/* Tipo de Operación */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Tipo de Operación
                </label>
                <select
                  value={data.caracteristicas.tipo_operacion}
                  onChange={(e) => handleSelectChange('tipo_operacion', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                >
                  {opcionesTipoOperacion.map((opcion) => (
                    <option key={opcion.valor} value={opcion.valor}>
                      {opcion.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ubicación */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Dirección o Zona *
                </label>
                <input
                  type="text"
                  name="ubicacion"
                  placeholder="Ej. Cholul, Mérida, Yucatán"
                  value={data.ubicacion}
                  onChange={handleChange}
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                />
              </div>

              {/* Zona */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Zona
                </label>
                <select
                  name="zona"
                  value={data.zona}
                  onChange={handleChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                >
                  <option value="Norte">Zona Norte</option>
                  <option value="Centro">Centro Histórico</option>
                  <option value="Sur">Zona Sur</option>
                  <option value="Poniente">Poniente</option>
                  <option value="Oriente">Oriente</option>
                  <option value="Nororiente">Nororiente</option>
                  <option value="Noroeste">Noroeste</option>
                </select>
              </div>

              {/* Uso Comercial */}
              <div className="flex items-center p-3 bg-slate-50 rounded-xl border border-slate-200">
                <input
                  type="checkbox"
                  id="uso_comercial"
                  name="uso_comercial"
                  checked={data.caracteristicas.uso_comercial}
                  onChange={handleCaracteristicaChange}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 border-slate-300"
                />
                <label htmlFor="uso_comercial" className="ml-3 text-sm text-slate-700">
                  Uso Comercial Permitido
                </label>
              </div>

              {/* Descripción */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Descripción Editorial
                </label>
                <textarea
                  name="descripcion"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm min-h-[140px] focus:bg-white focus:border-blue-500 transition-all outline-none resize-vertical"
                  placeholder="Describe la experiencia de vivir en esta propiedad, las amenidades destacadas, el entorno y cualquier información relevante..."
                  value={data.descripcion}
                  onChange={handleChange}
                  rows="5"
                />
              </div>
            </div>
          </section>

          {/* Tarjeta 2: Características Técnicas */}
          <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-slate-50 text-slate-600 rounded-lg">
                <Settings size={20} />
              </div>
              <h2 className="font-serif text-xl">Información Técnica</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Estado de Conservación */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Estado de Conservación
                </label>
                <select
                  value={data.caracteristicas.estado_conservacion}
                  onChange={(e) => handleSelectChange('estado_conservacion', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                >
                  {opcionesEstadoConservacion.map((opcion) => (
                    <option key={opcion.valor} value={opcion.valor}>
                      {opcion.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Antigüedad */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Antigüedad
                </label>
                <select
                  value={data.caracteristicas.antiguedad}
                  onChange={(e) => handleSelectChange('antiguedad', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                >
                  {opcionesAntiguedad.map((opcion) => (
                    <option key={opcion.valor} value={opcion.valor}>
                      {opcion.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tipo de Construcción */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Tipo de Construcción
                </label>
                <select
                  value={data.caracteristicas.tipo_construccion}
                  onChange={(e) => handleSelectChange('tipo_construccion', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                >
                  {opcionesTipoConstruccion.map((opcion) => (
                    <option key={opcion.valor} value={opcion.valor}>
                      {opcion.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Orientación */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Orientación
                </label>
                <input
                  type="text"
                  name="orientacion"
                  placeholder="Ej. Norte, Sur, etc."
                  value={data.caracteristicas.orientacion}
                  onChange={handleCaracteristicaChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                />
              </div>

              {/* Tipo de Agua */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 block">
                  Tipo de Agua
                </label>
                <select
                  value={data.caracteristicas.tipo_agua}
                  onChange={(e) => handleSelectChange('tipo_agua', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all outline-none"
                >
                  <option value="">Seleccionar...</option>
                  {opcionesTipoAgua.map((opcion) => (
                    <option key={opcion.valor} value={opcion.valor}>
                      {opcion.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Tarjeta 3: Características por grupos */}
          <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-slate-50 text-slate-600 rounded-lg">
                <Maximize2 size={20} />
              </div>
              <h2 className="font-serif text-xl">Características y Dimensiones</h2>
            </div>

            {gruposCaracteristicas.map((grupo, grupoIndex) => (
              <div key={grupoIndex} className="mb-8 last:mb-0">
                <div className="flex items-center gap-2 mb-4">
                  <div className="text-slate-600">{grupo.icono}</div>
                  <h3 className="text-lg font-medium text-slate-800">{grupo.titulo}</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {grupo.campos.map((campo, campoIndex) => (
                    <div
                      key={campoIndex}
                      className="bg-slate-50 border border-slate-200 rounded-xl p-4"
                    >
                      {campo.tipo === 'checkbox' ? (
                        <label className="flex items-center justify-between cursor-pointer">
                          <div className="flex items-center gap-2">
                            {campo.icono && <span className="text-slate-500">{campo.icono}</span>}
                            <span className="text-sm font-medium text-slate-700">
                              {campo.label}
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            name={campo.nombre}
                            checked={data.caracteristicas[campo.nombre]}
                            onChange={handleCaracteristicaChange}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 border-slate-300"
                          />
                        </label>
                      ) : (
                        <div>
                          <label className="text-xs font-medium text-slate-500 mb-1 block">
                            {campo.label}
                          </label>
                          <div className="flex items-center gap-2">
                            {campo.icono && <span className="text-slate-400">{campo.icono}</span>}
                            <input
                              type={campo.tipo}
                              name={campo.nombre}
                              value={data.caracteristicas[campo.nombre]}
                              onChange={handleCaracteristicaChange}
                              min={campo.min}
                              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              placeholder={campo.label}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          {/* Tarjeta 4: Multimedia con Tags */}
          <section className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-slate-50 text-slate-600 rounded-lg">
                <ImageIcon size={20} />
              </div>
              <h2 className="font-serif text-xl">Multimedia y Etiquetado</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Sección de imágenes */}
              <div className="space-y-6">
                {/* Imagen principal */}
                <div className="space-y-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
                    Imagen de Portada *
                  </div>
                  <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center hover:border-blue-400 transition-colors cursor-pointer bg-slate-50 relative overflow-hidden">
                    <input
                      type="file"
                      className="absolute inset-0 opacity-0 cursor-pointer z-10"
                      onChange={(e) => setFotoPrincipalArchivo(e.target.files[0])}
                      accept="image/*"
                      required
                    />
                    {fotoPrincipalArchivo ? (
                      <div className="relative">
                        <img
                          src={URL.createObjectURL(fotoPrincipalArchivo)}
                          className="w-full h-48 object-cover rounded-xl"
                          alt="Vista previa portada"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                          <Camera className="text-white" size={24} />
                          <span className="text-white text-sm ml-2">Cambiar imagen</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-slate-400 flex flex-col items-center gap-3 py-8">
                        <Upload size={32} strokeWidth={1} />
                        <span className="text-sm font-medium">Subir foto principal</span>
                        <span className="text-xs text-slate-500">
                          Recomendado: 1200x800px, max 15MB
                        </span>
                      </div>
                    )}
                  </div>
                  {fotoPrincipalArchivo && (
                    <div className="text-xs text-green-600 flex items-center gap-1">
                      <Check size={12} />
                      Imagen principal seleccionada: {fotoPrincipalArchivo.name}
                    </div>
                  )}
                </div>

                {/* Galería con tags */}
                <div className="space-y-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
                    Galería de Imágenes (máx. 30)
                  </div>
                  <ImageTagEditor images={imagenesConTags} onImagesChange={setImagenesConTags} />
                </div>
              </div>

              {/* Mapa */}
              <div className="space-y-6">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Ubicación en Mapa *
                </div>
                <div className="h-80 rounded-2xl overflow-hidden border border-slate-300">
                  <MapboxSelector
                    onSeleccionarUbicacion={handleSeleccionarUbicacion}
                    coordenadasIniciales={coordenadas}
                  />
                </div>

                {coordenadas ? (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center gap-2">
                      <Check size={12} /> Ubicación seleccionada
                    </div>
                    <div className="text-sm text-blue-600 mt-1">
                      Lat: {coordenadas[1].toFixed(6)}, Lng: {coordenadas[0].toFixed(6)}
                    </div>
                    <div className="text-xs text-blue-500 mt-1">Zona: {data.zona}</div>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <div className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-2">
                      <Info size={12} /> Instrucciones
                    </div>
                    <div className="text-sm text-amber-600 mt-1">
                      1. Haz click en el mapa para posicionar la propiedad
                    </div>
                    <div className="text-sm text-amber-600">
                      2. Arrastra el marcador para ajustar la ubicación exacta
                    </div>
                  </div>
                )}

                {/* Resumen de características */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                    Resumen de Características
                  </div>
                  <div className="text-sm text-slate-600 space-y-1">
                    <div>
                      • {data.caracteristicas.recamaras} recámaras, {data.caracteristicas.banos}{' '}
                      baños
                    </div>
                    <div>
                      • {data.caracteristicas.area} m² construcción, {data.caracteristicas.terreno}{' '}
                      m² terreno
                    </div>
                    <div>• {data.caracteristicas.estacionamiento} estacionamientos</div>
                    {Object.entries(data.caracteristicas)
                      .filter(
                        ([key, value]) =>
                          typeof value === 'boolean' && value && !['uso_comercial'].includes(key)
                      )
                      .slice(0, 3)
                      .map(([key]) => (
                        <div key={key}>• {key.replace('_', ' ')}</div>
                      ))}
                    {Object.entries(data.caracteristicas).filter(
                      ([key, value]) => typeof value === 'boolean' && value
                    ).length > 3 && (
                      <div className="text-slate-500 text-xs">
                        +
                        {Object.entries(data.caracteristicas).filter(
                          ([key, value]) => typeof value === 'boolean' && value
                        ).length - 3}{' '}
                        características más
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Botones */}
          <div className="flex justify-between items-center pt-8 border-t border-slate-100">
            <button
              type="button"
              onClick={() => navigate('/propiedades')}
              className="px-6 py-3 text-slate-600 hover:text-slate-900 font-medium rounded-xl hover:bg-slate-100 transition-colors"
              disabled={isSubmitting}
            >
              Cancelar
            </button>

            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-500 hidden md:block">
                {imagenesConTags.length} imágenes •{' '}
                {Object.values(data.caracteristicas).filter((v) => v === true).length}{' '}
                características
              </div>
              <button
                type="submit"
                disabled={!coordenadas || !fotoPrincipalArchivo || isSubmitting}
                className={`px-12 py-5 rounded-2xl font-bold uppercase tracking-[0.2em] text-xs flex items-center gap-3 shadow-xl transition-all ${
                  coordenadas && fotoPrincipalArchivo && !isSubmitting
                    ? 'bg-slate-900 text-white hover:bg-blue-700'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Publicando...
                  </>
                ) : (
                  <>
                    Publicar Propiedad
                    <ChevronRight
                      size={16}
                      className={
                        coordenadas && fotoPrincipalArchivo
                          ? 'group-hover:translate-x-1 transition-transform'
                          : 'opacity-50'
                      }
                    />
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
